import React, { useEffect, useRef, useState } from 'react';
import {
  format,
  parseISO,
  addMonths,
  subMonths,
  addYears,
  subYears,
  setMonth,
  setYear,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  isValid,
  getYear,
} from 'date-fns';
import { cn } from '../utils/cn';

const DISPLAY_FORMAT = 'dd-MMM-yyyy';
const VALUE_FORMAT = 'yyyy-MM-dd';

export interface DatePickerProps {
  value: string; // yyyy-MM-dd
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  className?: string;
}

function toDateOrToday(value: string): Date {
  if (!value) return new Date();
  const d = parseISO(value);
  return isValid(d) ? d : new Date();
}

const ChevronLeft = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRight = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const ChevronDown = () => (
  <svg className="h-4 w-4 flex-shrink-0 text-heritage-grey" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Select date',
  className,
}) => {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(() => toDateOrToday(value));
  const [viewMode, setViewMode] = useState<'date' | 'month' | 'year'>('date');
  const containerRef = useRef<HTMLDivElement>(null);

  const valueDate = value ? parseISO(value) : null;
  const displayText = value && isValid(parseISO(value))
    ? format(parseISO(value), DISPLAY_FORMAT)
    : '';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (value && isValid(parseISO(value))) {
      setViewMonth(parseISO(value));
    }
  }, [value]);

  const handleSelect = (d: Date) => {
    onChange(format(d, VALUE_FORMAT));
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setOpen(false);
  };

  const handleToday = () => {
    const today = new Date();
    onChange(format(today, VALUE_FORMAT));
    setViewMonth(today);
    setOpen(false);
  };

  const monthStart = startOfMonth(viewMonth);
  const monthEnd = endOfMonth(viewMonth);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {label && (
        <label className="block text-sm font-medium text-text-grey mb-1">{label}</label>
      )}
      <button
        type="button"
        onClick={() => {
          setOpen((prev) => {
            if (!prev) setViewMode('date');
            return !prev;
          });
        }}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg border border-[#E2E5EA] bg-white px-3 py-2.5 text-left text-sm shadow-sm',
          'focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent',
          'transition-colors hover:border-heritage-grey',
          !displayText && 'text-heritage-grey'
        )}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={cn(displayText ? 'text-text-grey' : 'text-heritage-grey')}>
          {displayText || placeholder}
        </span>
        <span className={cn('transition-transform duration-150', open && 'rotate-180')}>
          <ChevronDown />
        </span>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-40 mt-1 min-w-[280px] rounded-lg border border-[#E2E5EA] bg-white p-3 shadow-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <button
              type="button"
              onClick={() => {
                if (viewMode === 'date') setViewMonth((m) => subMonths(m, 1));
                else if (viewMode === 'month') setViewMonth((m) => subYears(m, 1));
                else setViewMonth((m) => subYears(m, 12));
              }}
              className="rounded p-1 text-heritage-grey hover:bg-page-bg hover:text-text-grey"
            >
              <ChevronLeft />
            </button>
            <div className="flex gap-1 items-center">
              <button
                type="button"
                onClick={() => {
                  if (viewMode === 'date') setViewMode('month');
                  else if (viewMode === 'month') setViewMode('year');
                  else setViewMode('month');
                }}
                className="rounded px-2 py-1 text-sm font-semibold text-blueprint-blue hover:bg-page-bg"
              >
                {viewMode === 'date' && format(viewMonth, 'MMMM')}
                {viewMode === 'month' && format(viewMonth, 'yyyy')}
                {viewMode === 'year' && `${getYear(viewMonth) - 11} – ${getYear(viewMonth)}`}
              </button>
              {viewMode === 'date' && (
                <button
                  type="button"
                  onClick={() => setViewMode('year')}
                  className="rounded px-2 py-1 text-sm font-semibold text-blueprint-blue hover:bg-page-bg"
                >
                  {format(viewMonth, 'yyyy')}
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                if (viewMode === 'date') setViewMonth((m) => addMonths(m, 1));
                else if (viewMode === 'month') setViewMonth((m) => addYears(m, 1));
                else setViewMonth((m) => addYears(m, 12));
              }}
              className="rounded p-1 text-heritage-grey hover:bg-page-bg hover:text-text-grey"
            >
              <ChevronRight />
            </button>
          </div>

          {/* Month view */}
          {viewMode === 'month' && (
            <div className="grid grid-cols-3 gap-1 mb-2">
              {Array.from({ length: 12 }, (_, i) => {
                const monthDate = setMonth(new Date(getYear(viewMonth), 0, 1), i);
                const isSelected = valueDate !== null && isValid(valueDate) && isSameMonth(monthDate, valueDate);
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      setViewMonth(setMonth(viewMonth, i));
                      setViewMode('date');
                    }}
                    className={cn(
                      'rounded py-2 text-sm font-medium',
                      isSelected
                        ? 'bg-primary text-white hover:bg-primary-600'
                        : 'text-text-grey hover:bg-page-bg'
                    )}
                  >
                    {format(monthDate, 'MMM')}
                  </button>
                );
              })}
            </div>
          )}

          {/* Year view */}
          {viewMode === 'year' && (
            <div className="grid grid-cols-3 gap-1 mb-2">
              {Array.from({ length: 12 }, (_, i) => {
                const y = getYear(viewMonth) - 11 + i;
                const isSelected = valueDate !== null && isValid(valueDate) && getYear(valueDate) === y;
                return (
                  <button
                    key={y}
                    type="button"
                    onClick={() => {
                      setViewMonth(setYear(viewMonth, y));
                      setViewMode('month');
                    }}
                    className={cn(
                      'rounded py-2 text-sm font-medium',
                      isSelected
                        ? 'bg-primary text-white hover:bg-primary-600'
                        : 'text-text-grey hover:bg-page-bg'
                    )}
                  >
                    {y}
                  </button>
                );
              })}
            </div>
          )}

          {/* Date view */}
          {viewMode === 'date' && (
            <div className="grid grid-cols-7 gap-0.5 mb-2">
              {weekDays.map((day, i) => (
                <div key={i} className="py-1 text-center text-xs font-medium text-heritage-grey">
                  {day}
                </div>
              ))}
              {days.map((day) => {
                const sameMonth = isSameMonth(day, viewMonth);
                const selected = valueDate !== null && isValid(valueDate) && isSameDay(day, valueDate);
                const todayDay = isToday(day);
                return (
                  <button
                    key={day.toISOString()}
                    type="button"
                    onClick={() => handleSelect(day)}
                    className={cn(
                      'rounded py-1.5 text-sm',
                      !sameMonth && 'text-[#C7CCD3]',
                      sameMonth && !selected && 'text-text-grey hover:bg-page-bg',
                      selected && 'bg-primary text-white hover:bg-primary-600',
                      !selected && todayDay && sameMonth && 'font-semibold ring-1 ring-primary ring-inset'
                    )}
                  >
                    {format(day, 'd')}
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex gap-2 border-t border-[#EEF0F3] pt-2">
            <button
              type="button"
              onClick={handleClear}
              className="flex-1 rounded px-2 py-1.5 text-sm text-heritage-grey hover:bg-page-bg"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={handleToday}
              className="flex-1 rounded px-2 py-1.5 text-sm text-primary hover:bg-primary-50 font-medium"
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
